import { T, useT } from '@transifex/react';
import { Text, VStack } from '@vidahealth/vida-ui';
import { useEffect, useState } from 'react';
import { View } from 'react-native';

import { BottomSheet, CtaButton, ScreenHeading } from '@/modules/ui';

import { getFootnoteHints, getWarningCopy } from '../constants/verificationCopy';
import type { DialogState, Surface } from '../types';

import { AddressSummary } from './AddressSummary';
import { ConfirmAddressCards, type AddressChoice } from './ConfirmAddressCards';

type Props = {
  state: DialogState;
  surface: Surface;
};

/**
 * Renders the address-verification dialog for the current `dialogState` from
 * `useAddressVerification`, as a bottom sheet (matching onboarding's overlay
 * pattern rather than VRN's centered modal). One sheet whose content + footer
 * switch on `state.kind`; each footer button invokes the callback the hook
 * attached to that state, which resolves the pending `verify()` promise.
 */
export function VerificationDialogs({ state, surface }: Props) {
  const t = useT();
  const testIDPrefix = `${surface.replaceAll('_', '-')}-address`;
  const [confirmChoice, setConfirmChoice] = useState<AddressChoice>('verified');

  // Backdrop / drag dismiss maps to the current kind's cancel/close action.
  const onDismiss = () => {
    switch (state.kind) {
      case 'confirm':
      case 'warning':
        state.onCancel();
        return;
      case 'undeliverable':
      case 'error':
        state.onClose();
        return;
      case 'idle':
        return;
    }
  };

  // Reset the suggested-vs-typed selection each time a confirm dialog opens.
  useEffect(() => {
    if (state.kind === 'confirm') setConfirmChoice('verified');
  }, [state.kind]);

  const content = ((): { body: React.ReactNode; footer: React.ReactNode } => {
    switch (state.kind) {
      case 'idle':
        return { body: null, footer: null };

      case 'confirm': {
        const testID = `${testIDPrefix}-confirm`;
        return {
          body: (
            <VStack space='md'>
              <ScreenHeading
                title={t('Confirm your address')}
                subtitle={t('We found a more complete version. Pick the one to use.')}
              />
              <ConfirmAddressCards
                user={state.user}
                verified={state.verified}
                selected={confirmChoice}
                onSelect={setConfirmChoice}
                testID={testID}
              />
            </VStack>
          ),
          footer: (
            <VStack space='md'>
              <CtaButton
                onPress={confirmChoice === 'verified' ? state.onUseVerified : state.onUseEntered}
                testID={`${testID}-accept`}
              >
                <T _str='Use this address' />
              </CtaButton>
              <CtaButton type='secondary' onPress={state.onCancel} testID={`${testID}-edit`}>
                <T _str='Edit what I typed' />
              </CtaButton>
            </VStack>
          ),
        };
      }

      case 'warning': {
        const testID = `${testIDPrefix}-warning`;
        const { title, description } = getWarningCopy(state.deliverability, t);
        return {
          body: (
            <VStack space='md'>
              <ScreenHeading title={title} subtitle={description} />
              <AddressSummary address={state.address} testID={`${testID}-address`} />
            </VStack>
          ),
          footer: (
            <VStack space='md'>
              <CtaButton onPress={state.onCancel} testID={`${testID}-cancel`}>
                <T _str='Make changes' />
              </CtaButton>
              <CtaButton type='secondary' onPress={state.onContinue} testID={`${testID}-continue`}>
                <T _str='Use anyway' />
              </CtaButton>
            </VStack>
          ),
        };
      }

      case 'undeliverable': {
        const testID = `${testIDPrefix}-undeliverable`;
        const hints = getFootnoteHints(state.footnoteCodes, t);
        return {
          body: (
            <VStack space='md'>
              <ScreenHeading title={t("Hm, this address doesn't look valid")} />
              <AddressSummary address={state.address} testID={`${testID}-address`} />
              {hints.length > 0 ? (
                <View className='gap-1'>
                  {hints.map((hint, index) => (
                    // testID on the View, not the vida-ui Text (its web build leaks
                    // unknown props onto the DOM node).
                    <View key={`${index}:${hint}`} testID={testID + '-footnote-' + index}>
                      <Text size='sm' className='text-typography-600'>
                        {'• ' + hint}
                      </Text>
                    </View>
                  ))}
                </View>
              ) : null}
            </VStack>
          ),
          footer: (
            <VStack space='md'>
              <CtaButton onPress={state.onClose} testID={`${testID}-edit`}>
                <T _str='Make changes' />
              </CtaButton>
              <CtaButton
                type='secondary'
                onPress={state.onSubmitAnyway}
                testID={`${testID}-submit`}
              >
                <T _str='Use anyway' />
              </CtaButton>
            </VStack>
          ),
        };
      }

      case 'error': {
        const testID = `${testIDPrefix}-error`;
        return {
          body: (
            <VStack space='md'>
              <ScreenHeading
                title={t('Could not verify address')}
                subtitle={t(
                  "We couldn't reach our address verification service right now. Please try again."
                )}
              />
              <AddressSummary address={state.address} testID={`${testID}-address`} />
            </VStack>
          ),
          footer: (
            <VStack space='md'>
              <CtaButton onPress={state.onSubmitAnyway} testID={`${testID}-submit`}>
                <T _str='Submit without verification' />
              </CtaButton>
              <CtaButton type='secondary' onPress={state.onClose} testID={`${testID}-dismiss`}>
                <T _str='Cancel' />
              </CtaButton>
            </VStack>
          ),
        };
      }
    }
  })();

  return (
    <BottomSheet isOpen={state.kind !== 'idle'} onClose={onDismiss} footer={content.footer}>
      {content.body}
    </BottomSheet>
  );
}
